<template>
  <div v-if="!hasData" class="empty-state">
    <p class="empty-state__title">暂无门店与品牌信息</p>
    <p class="empty-state__sub">请确认门店编码是否正确，或稍后重试</p>
  </div>

  <div v-else class="overview">
    <!-- 品牌信息 -->
    <div class="card">
      <div class="card__head">
        <span class="card__title">品牌信息</span>
        <span class="tag tag-info">{{ brandInfo.brandCode || '—' }}</span>
      </div>
      <div class="info-grid">
        <div><div class="info-label">品牌名称</div><div class="info-value">{{ dash(brandInfo.brandName) }}</div></div>
        <div><div class="info-label">品牌编码</div><div class="info-value num">{{ dash(brandInfo.brandCode) }}</div></div>
        <div><div class="info-label">创始人</div><div class="info-value">{{ dash(brandInfo.founder) }}</div></div>
        <div><div class="info-label">联系方式</div><div class="info-value num">{{ dash(brandInfo.founderPhone) }}</div></div>
        <div><div class="info-label">首店开业时间</div><div class="info-value num">{{ dash(formatDate(brandInfo.firstStoreOpenTime)) }}</div></div>
        <div><div class="info-label">实际地址</div><div class="info-value">{{ dash(brandInfo.entDetailAddress) }}</div></div>
      </div>

      <el-collapse v-model="brandOpen" class="mt12">
        <el-collapse-item title="品牌工商信息" name="brand">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="企业名称">{{ dash(brandCompanyInfo.name) }}</el-descriptions-item>
            <el-descriptions-item label="统一社会信用代码">{{ dash(brandInfo.brandBusinessLicense) }}</el-descriptions-item>
            <el-descriptions-item label="法定代表人">{{ dash(brandCompanyInfo.operName) }}</el-descriptions-item>
            <el-descriptions-item label="注册资本">{{ formatCapital(brandCompanyInfo) }}</el-descriptions-item>
            <el-descriptions-item label="成立日期">{{ dash(formatDate(brandCompanyInfo.startDate)) }}</el-descriptions-item>
            <el-descriptions-item label="企业类型">{{ dash(brandCompanyInfo.econKind) }}</el-descriptions-item>
            <el-descriptions-item label="登记状态">{{ dash(brandCompanyInfo.status) }}</el-descriptions-item>
            <el-descriptions-item label="注册地址" :span="2">{{ dash(brandCompanyInfo.address) }}</el-descriptions-item>
            <el-descriptions-item label="经营范围" :span="2">{{ dash(brandCompanyInfo.scope) }}</el-descriptions-item>
          </el-descriptions>
        </el-collapse-item>
      </el-collapse>
    </div>

    <!-- 门店信息 -->
    <div class="card">
      <div class="card__head">
        <span class="card__title">门店信息</span>
        <button v-if="canEdit" class="btn btn-sm" @click="$emit('edit')">编辑</button>
      </div>
      <div class="info-grid">
        <div><div class="info-label">门店名称</div><div class="info-value">{{ dash(storeInfo.storeName) }}</div></div>
        <div><div class="info-label">门店编码</div><div class="info-value num">{{ dash(storeInfo.storeEntCode) }}</div></div>
        <div><div class="info-label">开店模式</div><div class="info-value">{{ storeInfo.storePattern === 'direct' ? '直营' : dash(storeInfo.storePattern) }}</div></div>
        <div><div class="info-label">所在地区</div><div class="info-value">{{ [storeInfo.province, storeInfo.city].filter(Boolean).join(' ') || '—' }}</div></div>
        <div><div class="info-label">租期时长</div><div class="info-value num">{{ storeInfo.rentTerm ? storeInfo.rentTerm + ' 天' : '—' }}</div></div>
        <div><div class="info-label">开店投资金额</div><div class="info-value num">{{ storeInfo.investAmountForOpening ? '¥' + formatCurrency(storeInfo.investAmountForOpening) : '—' }}</div></div>
        <div><div class="info-label">租期开始</div><div class="info-value num">{{ dash(formatDate(storeInfo.groundLeaseTermBegin)) }}</div></div>
        <div><div class="info-label">租期结束</div><div class="info-value num">{{ dash(formatDate(storeInfo.groundLeaseTermEnd)) }}</div></div>
        <div class="span-2"><div class="info-label">详细地址</div><div class="info-value">{{ dash(storeInfo.entDetailAddress) }}</div></div>
      </div>

      <el-collapse v-model="storeOpen" class="mt12">
        <el-collapse-item title="门店工商信息" name="store">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="企业名称">{{ dash(storeInfo.storeEntName) }}</el-descriptions-item>
            <el-descriptions-item label="统一社会信用代码">{{ dash(storeInfo.storeBusinessLicense) }}</el-descriptions-item>
            <el-descriptions-item label="法定代表人">{{ dash(storeCompanyInfo.operName) }}</el-descriptions-item>
            <el-descriptions-item label="注册资本">{{ formatCapital(storeCompanyInfo) }}</el-descriptions-item>
            <el-descriptions-item label="成立日期">{{ dash(formatDate(storeCompanyInfo.startDate)) }}</el-descriptions-item>
            <el-descriptions-item label="企业类型">{{ dash(storeCompanyInfo.econKind) }}</el-descriptions-item>
            <el-descriptions-item label="登记状态">{{ dash(storeCompanyInfo.status) }}</el-descriptions-item>
            <el-descriptions-item label="注册地址" :span="2">{{ dash(storeCompanyInfo.address) }}</el-descriptions-item>
            <el-descriptions-item label="经营范围" :span="2">{{ dash(storeCompanyInfo.scope) }}</el-descriptions-item>
          </el-descriptions>
        </el-collapse-item>
      </el-collapse>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { formatDate, formatCurrency, formatCapital, dash } from './formatters'

export default {
  name: 'StoreOverviewTab',
  props: {
    brandInfo: { type: Object, default: () => ({}) },
    storeInfo: { type: Object, default: () => ({}) },
    brandCompanyInfo: { type: Object, default: () => ({}) },
    storeCompanyInfo: { type: Object, default: () => ({}) },
    canEdit: { type: Boolean, default: false }
  },
  emits: ['edit'],
  setup(props) {
    const brandOpen = ref([])
    const storeOpen = ref([])

    // 品牌与门店都取不到数据时给出明确提示，而不是一片空白
    const hasData = computed(
      () => Object.keys(props.storeInfo || {}).length > 0 ||
            Object.keys(props.brandInfo || {}).length > 0
    )

    return {
      brandOpen, storeOpen, hasData,
      formatDate, formatCurrency, formatCapital, dash
    }
  }
}
</script>

<style scoped>
.overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  background: var(--bg-card);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-card);
  padding: 48px 20px;
  text-align: center;
}

.empty-state__title {
  font-size: 13px;
  color: var(--text-secondary);
}

.empty-state__sub {
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 14px 16px;
}

.card__head {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.card__title {
  font-size: 14px;
  font-weight: 500;
}

.card__head .tag {
  margin-left: 8px;
}

.card__head .btn {
  margin-left: auto;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 13px 16px;
}

.info-grid .span-2 {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 11px;
  color: var(--text-tertiary);
}

.info-value {
  font-size: 13px;
  margin-top: 2px;
  word-break: break-all;
}

.tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: var(--radius-tag);
}

.tag-info { background: var(--info-bg); color: var(--info-text); }

.mt12 { margin-top: 12px; }

.btn {
  padding: 5px 12px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-sm { padding: 4px 10px; }
</style>
